import React, { createContext, useContext, useState, useEffect } from 'react';
import { doc, setDoc, getDoc } from 'firebase/firestore';
import { db, auth } from '../firebase/config';

const PushNotificationContext = createContext();

export const usePushNotification = () => {
  const context = useContext(PushNotificationContext);
  if (!context) {
    throw new Error('usePushNotification must be used within a PushNotificationProvider');
  }
  return context;
};

export const PushNotificationProvider = ({ children }) => {
  // Initialiser avec 'default' si Notification n'est pas disponible
  const getInitialPermission = () => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      return Notification.permission || 'default';
    }
    return 'default';
  };

  const [permission, setPermission] = useState(getInitialPermission());
  const [subscription, setSubscription] = useState(null);

  // Charger l'abonnement existant au démarrage
  useEffect(() => {
    const loadExistingSubscription = async () => {
      if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
        return;
      }

      try {
        const registration = await navigator.serviceWorker.ready;
        const existingSubscription = await registration.pushManager.getSubscription();
        if (existingSubscription) {
          setSubscription(existingSubscription);
        }
      } catch (error) {
        console.error('Erreur lors du chargement de l\'abonnement existant:', error);
      }
    };

    loadExistingSubscription();
  }, []);

  // Vérifier la permission
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window) {
      setPermission(Notification.permission);
    }
  }, []);

  // Demander la permission
  const requestPermission = async () => {
    if (!('Notification' in window)) {
      console.warn('Ce navigateur ne supporte pas les notifications');
      return false;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermission(permission);
      return permission === 'granted';
    } catch (error) {
      console.error('Erreur lors de la demande de permission:', error);
      return false;
    }
  };

  // Envoyer une notification
  const sendNotification = (title, options = {}) => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('Ce navigateur ne supporte pas les notifications');
      return;
    }

    if (permission !== 'granted') {
      console.warn('Permission de notification non accordée');
      return;
    }

    try {
      new Notification(title, {
        icon: '/logo.png',
        badge: '/logo.png',
        ...options
      });
    } catch (error) {
      console.error('Erreur lors de l\'envoi de la notification:', error);
    }
  };

  // Fonction utilitaire pour convertir la clé VAPID base64 en Uint8Array
  const urlBase64ToUint8Array = (base64String) => {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
      .replace(/-/g, '+')
      .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
      outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
  };

  // S'abonner aux notifications push
  const subscribeToPush = async () => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.warn('Les notifications push ne sont pas supportées');
      return false;
    }

    if (permission !== 'granted') {
      console.warn('La permission de notification n\'est pas accordée');
      return false;
    }

    try {
      // Obtenir la clé publique VAPID depuis les variables d'environnement
      const vapidPublicKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
      
      if (!vapidPublicKey) {
        console.error('REACT_APP_VAPID_PUBLIC_KEY n\'est pas définie');
        throw new Error('Clé VAPID publique non configurée');
      }

      // Attendre que le service worker soit prêt
      const registration = await navigator.serviceWorker.ready;
      
      // Vérifier si on a déjà un abonnement
      let subscription = await registration.pushManager.getSubscription();
      
      // Si pas d'abonnement, créer un nouvel abonnement
      if (!subscription) {
        subscription = await registration.pushManager.subscribe({
          userVisibleOnly: true,
          applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
        });
      }

      // Sauvegarder l'abonnement dans Firestore si l'utilisateur est connecté
      const currentUser = auth.currentUser;
      
      if (currentUser) {
        try {
          const flat = {
            endpoint: subscription.endpoint,
            keys: {
              p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
              auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
            }
          };
          const subscriptionData = {
            ...flat,
            // Nested (compat serveur) + plat (compat ancien code)
            subscription: flat,
            userId: currentUser.uid,
            userEmail: currentUser.email || null,
            enabled: true,
            topics: ['offers', 'new_business', 'advantages', 'activities'],
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString()
          };

          // Sauvegarder dans Firestore sous /pushSubscriptions/{userId}
          await setDoc(doc(db, 'pushSubscriptions', currentUser.uid), subscriptionData, { merge: true });
          console.log('Abonnement sauvegardé dans Firestore');
        } catch (error) {
          console.error('Erreur lors de la sauvegarde de l\'abonnement:', error);
          // Ne pas faire échouer l'abonnement si la sauvegarde échoue
        }
      }

      setSubscription(subscription);
      return true;
    } catch (error) {
      console.error('Erreur lors de l\'abonnement aux notifications push:', error);
      return false;
    }
  };

  // Se désabonner des notifications push
  const unsubscribeFromPush = async () => {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator) || !('PushManager' in window)) {
      return false;
    }

    try {
      const registration = await navigator.serviceWorker.ready;
      const currentSubscription = await registration.pushManager.getSubscription();
      if (currentSubscription) {
        await currentSubscription.unsubscribe();
        setSubscription(null);

        // Supprimer l'abonnement de Firestore
        const currentUser = auth.currentUser;
        if (currentUser) {
          try {
            await setDoc(doc(db, 'pushSubscriptions', currentUser.uid), {
              deleted: true,
              deletedAt: new Date().toISOString()
            }, { merge: true });
          } catch (error) {
            console.error('Erreur lors de la suppression de l\'abonnement dans Firestore:', error);
          }
        }

        return true;
      }
      return false;
    } catch (error) {
      console.error('Erreur lors du désabonnement des notifications push:', error);
      return false;
    }
  };

  const value = {
    permission,
    subscription,
    requestPermission,
    sendNotification,
    subscribeToPush,
    unsubscribeFromPush
  };

  return (
    <PushNotificationContext.Provider value={value}>
      {children}
    </PushNotificationContext.Provider>
  );
};

